import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { FiSearch, FiArrowLeft } from 'react-icons/fi';
import { Icon } from '@iconify/react';
import 'flag-icons/css/flag-icons.min.css';

const Container = styled.div`
  display: flex;
  gap: 2rem;
  height: 100%;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const CountriesSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-width: 0;
`;

const Sidebar = styled.div`
  flex: 0 0 350px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const BackButton = styled.button`
  background: #f1f5f9;
  border: none;
  border-radius: 12px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #e2e8f0;
    transform: translateY(-1px);
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  margin: 0 0 0.25rem 0;
  color: #334155;
  font-size: 1.3rem;
`;

const Subtitle = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IngredientIcon = styled.span`
  font-size: 1.1rem;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const LetterFilterContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
`;

const LetterButton = styled(motion.button)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid ${props => props.active ? '#3b82f6' : '#e2e8f0'};
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#334155'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #3b82f6;
    background: ${props => props.active ? '#1d4ed8' : '#eff6ff'};
    transform: scale(1.1);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  background: #f8fafc;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 1.2rem;
`;

const CountriesContainer = styled.div`
  flex: 1;
  min-height: 0;
`;

const ScrollableArea = styled.div`
  height: 100%;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 0.5rem;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #3b82f6;
    border-radius: 4px;
  }
`;

const ContinentSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContinentHeader = styled.h3`
  color: #3b82f6;
  font-size: 1.1rem;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #3b82f6;
  font-weight: 600;
  background: #f8fafc;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
`;

const CountriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const CountryButton = styled(motion.button)`
  background: #f8fafc;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
  aspect-ratio: 1;
  min-height: 120px;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
  }
`;

const CountryFlag = styled.span`
  width: 96px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  display: inline-block;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &.fi {
    background-size: cover;
    background-position: center;
  }
`;

const CountryName = styled.span`
  font-weight: 600;
  color: #334155;
  font-size: 1.05rem;
  line-height: 1.2;
  word-break: break-word;
`;

const NoResults = styled(motion.div)`
  text-align: center;
  padding: 3rem;
  color: #64748b;
  font-style: italic;
`;

const IngredientInfo = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const IngredientInfoTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #334155;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IngredientDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
`;

const IngredientLetter = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const IngredientDetails = styled.div`
  flex: 1;
`;

const IngredientName = styled.div`
  font-weight: 600;
  color: #334155;
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const IngredientEmoji = styled.span`
  font-size: 1.5rem;
  margin-right: 0.5rem;
`;

const IngredientJapaneseName = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-style: italic;
`;

const IngredientDescription = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
  margin-bottom: 1rem;
`;

function CountrySelector({ ingredient, countries, onSelectCountry, onBack }) {
  const [selectedLetter, setSelectedLetter] = useState(null);

  const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const filteredCountries = useMemo(() => {
    const toFilter = selectedLetter
      ? countries.filter(country => country.name.charAt(0).toUpperCase() === selectedLetter)
      : countries;
    return toFilter.sort((a, b) => a.name.localeCompare(b.name));
  }, [countries, selectedLetter]);

  return (
    <Container>
      <CountriesSection>
        <Header>
          <BackButton onClick={onBack}>
            <FiArrowLeft size={20} />
          </BackButton>
          <HeaderContent>
            <Title>Choose Country</Title>
            <Subtitle>
              <IngredientIcon>
                {ingredient?.iconType === 'image' ? (
                  <img 
                    src={ingredient.icon} 
                    alt={ingredient.name}
                    style={{
                      width: '1.5em',
                      height: '1.5em',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <Icon icon={ingredient?.icon} width="1.5em" height="1.5em" />
                )}
              </IngredientIcon>
              {ingredient?.name} is from...
            </Subtitle>
          </HeaderContent>
        </Header>

        <LetterFilterContainer>
          {allLetters.map(letter => (
            <LetterButton
              key={letter}
              active={selectedLetter === letter}
              onClick={() => setSelectedLetter(selectedLetter === letter ? null : letter)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {letter}
            </LetterButton>
          ))}
        </LetterFilterContainer>

        <CountriesContainer>
          <ScrollableArea>
            <CountriesGrid>
                <AnimatePresence>
                  {filteredCountries.map((country, index) => (
                    <CountryButton
                      key={country.code}
                      onClick={() => onSelectCountry(country)}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ delay: index * 0.02 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <CountryFlag className={`fi fi-${country.code}`} />
                      <CountryName>{country.name}</CountryName>
                    </CountryButton>
                  ))}
                </AnimatePresence>
              </CountriesGrid>
          </ScrollableArea>
        </CountriesContainer>
      </CountriesSection>

      <Sidebar>
        <IngredientInfo
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <IngredientInfoTitle>
            🥘 Ingredient
          </IngredientInfoTitle>
          
          <IngredientDisplay>
            <IngredientLetter>{ingredient?.letter}</IngredientLetter>
            <IngredientDetails>
              <IngredientName>
                <IngredientEmoji>
                  {ingredient?.iconType === 'image' ? (
                    <img 
                      src={ingredient.icon} 
                      alt={ingredient.name}
                      style={{
                        width: '1em',
                        height: '1em',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <Icon icon={ingredient?.icon} width="1em" height="1em" />
                  )}
                </IngredientEmoji>
                {ingredient?.name}
              </IngredientName>
              {ingredient?.japanese && (
                <IngredientJapaneseName>{ingredient.japanese}</IngredientJapaneseName>
              )}
              {ingredient?.description && (
                <IngredientDescription>{ingredient.description}</IngredientDescription>
              )}
              <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                Choose a country
              </div>
            </IngredientDetails>
          </IngredientDisplay>
        </IngredientInfo>
      </Sidebar>
    </Container>
  );
}

export default CountrySelector;
